import React, {useContext, useState} from 'react'
import clsx from 'clsx'
import {CustomerContext} from '../CustomerContext'

import {Card} from '@/components/Card'
import {Alert} from '@/components/Alert'
import {useIntl, IntlShape} from 'react-intl'
import {EmailsCard} from './cards/EmailsCard'
import {StatusesCard} from './cards/StatusesCard'
import {RiskCard} from './cards/RiskCard'
import {IndicatorCard} from './cards/IndicatorCard'
import SharedUsersCard from './cards/SharedUsersCard'
import {Device, Isp, Msisdn, IpData} from '../../core/_models'

interface MsisdnListProps {
  msisdns: Msisdn[];
}

const formatDate = (intl: IntlShape, date: string|Date) => {
  return intl.formatDate(date, {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
  })
}

const normalizeCarrier = (carrier?: string): string|undefined => {
  if (!carrier) return "N/A";

  switch (carrier) {
    case "Société française du radiotéléphone":
      return "SFR";
    default:
      return carrier;
  }
}

const MsisdnList: React.FC<MsisdnListProps> = ({ msisdns }) => {
  const intl = useIntl();
  const [copied, setCopied] = useState<string | undefined>();
  const [visibleCount, setVisibleCount] = useState(12);
  const formatNumber = (value: string): string =>
    value.replace(/(.{2})/g, "$1 ").trim();

  const sorted = [...msisdns].sort((a, b) => {
    const timeA = a.validatedAt ? new Date(a.validatedAt).getTime() : 0;
    const timeB = b.validatedAt ? new Date(b.validatedAt).getTime() : 0;
    return timeB - timeA;
  });
  
  const handleCopy = (text: string | undefined) => {
    if (text === undefined) return;
    navigator.clipboard.writeText(text);
    setCopied(text);
    setTimeout(() => setCopied(undefined), 2000);
  }

  const copiedTooltip = () => (
    <span className="position-absolute translate-middle-x bg-dark text-white px-2 py-1 rounded ms-2 fs-8 z-index-1">
      Copied!
    </span>
  )

  return (
    <Card
      id="msisdns"
      className=" mb-5"
      collapsable={true}
      collapse={msisdns.length > 8 || msisdns.length === 0}
      title={
        <div className="d-flex align-items-center">
          <span className="me-2">Msisdn</span>
          <span className="badge bg-primary text-white">
            {msisdns.length} used
          </span>
        </div>
      }
    >
      <div className="mb-5 p-3 border rounded bg-light">
        <h5>Explanation of MSISDN status:</h5>
        <ul>
          <li>
            <span className="badge badge-success">Activated without a token</span>:{' '}
            The user has added a line but has never made a payment.
          </li>
          <li>
            <span className="badge badge-warning">Disabled without a token</span>:{' '}
            The user attempted to make a payment with a number that is already in use.
          </li>
          <li>
            <span className="badge badge-info">Disabled with a token</span>:{' '}
            The user made a payment with a number already assigned to another user, or has deactivated the line from their account.
          </li>
        </ul>
        <i className="text-muted">
          Tokens represent the code of the first transaction. Cases 2 and 3 should no longer occur for accounts created after the Node.js migration.
        </i>

        <p className="mt-2">
          <i className="text-muted">
            The displayed numbers correspond to <b>Premium Calling</b> and <b>DCB</b> payment solutions.{' '}
            For <b>Premium Text Message</b> payments, only an alias is available.
          </i>
        </p>
      </div>
      <div className="row g-3">
        {sorted.slice(0, visibleCount).map((msisdn, index) => (
          <div className="col-12 col-sm-6 col-md-4 col-lg-3" key={index}>
            <div className={clsx('card', {
              'bg-success text-white': msisdn.enabled,
              'bg-warning text-white': !msisdn.enabled && !msisdn.token,
              'bg-info text-white': !msisdn.enabled && msisdn.token,
            })}>
              <div className="p-3">
                <i className="">
                  <b>Label: </b> {msisdn?.label ?? '-'}
                </i>
                <div className="d-flex justify-content-between mb-1">
                  <span
                    className="fw-bold fs-3 cursor-pointer"
                    onClick={() => handleCopy(msisdn.value)}
                  >
                    {formatNumber(msisdn.value)}
                    {copied !== undefined && copied === msisdn.value && (copiedTooltip())}
                  </span>
                  <span className="fw-bold fs-4" title="Carrier">
                    {normalizeCarrier(msisdn.carrier)}
                  </span>
                  <span className={clsx('badge', {
                    'badge-light-success': msisdn.enabled,
                    'badge-light-warning': !msisdn.enabled && !msisdn.token,
                    'badge-light-info': !msisdn.enabled && msisdn.token
                  })}>
                    {msisdn.enabled ? 'ACTIVE' : 'DISABLED'}
                  </span>
                </div>

                <div className="d-flex justify-content-between mt-1">
                  <span>
                    {msisdn.validatedAt
                      ? intl.formatDate(msisdn.validatedAt, {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                          hour: "numeric",
                          minute: "numeric",
                          second: "numeric",
                        })
                      : "-"}
                  </span>
                  <span
                    className={clsx('fw-bold fs-4', {
                      'cursor-pointer': msisdn.token
                    })}
                    onClick={() => handleCopy(msisdn.token)}
                  >
                    {msisdn.token || "N/A"}
                    {copied !== undefined && copied === msisdn.token && (copiedTooltip())}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {visibleCount < msisdns.length && (
        <div className="d-flex justify-content-center mt-4">
          <button
            className="btn btn-primary"
            type="button"
            onClick={() => setVisibleCount(visibleCount + 12)}
          >
            Load More
          </button>
        </div>
      )}
    </Card>
  );
};

export function Overview() {
  const { customer, indicators } = useContext(CustomerContext)
  const intl = useIntl()

  if (!customer) {
    return (<></>)
  }
  const status = customer.statuses.at(-1)
  const margin = (indicators.earnings.trx + indicators.earnings.purchase) * 100 / (indicators.payouts || 1)

  return (
    <>
      {status?.status === 'LOCKED' && (
        <Alert
          title={`This account is locked since ${intl.formatDate(status?.date, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric'
          })}`}
          message={status?.reason}
          type="danger"
          icon="lock-3"
        />
      )}

      <div className="d-flex flex-column flex-lg-row">

        {/* Begin left col */}
        <div className="d-flex flex-column gap-7 gap-lg-10 w-100 w-lg-350px mb-7 me-lg-10">
          <EmailsCard />
          <StatusesCard />
        </div>
        {/* End left col */}


        {/* Begin right col */}
        <div className="d-flex flex-column flex-row-fluid gap-7 gap-lg-10">
          {/* Begin indicators */}
          <div className="row">
            <div className="col-xl-3">
              <IndicatorCard
                value={intl.formatNumber(
                  indicators.earnings.trx + indicators.earnings.purchase,
                  { style: 'currency', currency: 'EUR' }
                )}
                title={
                  <div className="d-flex justify-content-between align-items-center">
                    <span>Earnings</span>
                    <div className="fw-semibold text-white fs-7" title="Include not spent credits">
                      <b>{margin.toFixed(2)}%</b> margin
                    </div>
                  </div>
                }

                icon="chart-pie-simple"
                color="success"
              />
            </div>
            <div className="col-xl-3">
              <IndicatorCard
                value={intl.formatNumber(indicators.paid, { style: 'currency', currency: 'EUR' })}
                title={
                  <div className="d-flex justify-content-between align-items-center">
                    <span>Paid</span>
                    <div className="fw-semibold text-white fs-7">
                      {indicators.trxs} transactions
                    </div>
                  </div>
                }
                icon="euro"
                color="danger"
              />
            </div>

            <div className="col-xl-3">
              <IndicatorCard
                value={intl.formatNumber(indicators.payouts, { style: 'currency', currency: 'EUR' })}
                title="Payouts"
                icon="chart-pie-too"
                color="primary"
              />
            </div>

            <div className="col-xl-3">
              <IndicatorCard
                value={intl.formatNumber(indicators.purchases)}
                title="Purchases"
                icon="handcart"
                color="info"
              />
            </div>
          </div>
          {/* End indicators */}

          {/* Start profile details */}
          <Card
            title='Profile'
            className="mb-1"
            collapsable={false}
          >
            <div className='row mb-7'>
              <label className='col-lg-4 fw-bold text-muted'>
                Username
              </label>

              <div className='col-lg-8'>
                <span className='fw-bolder fs-6 text-dark'>{customer.username}</span>
              </div>
            </div>

            <div className='row mb-7'>
              <label className='col-lg-4 fw-bold text-muted'>
                Fullname
              </label>

              <div className='col-lg-8'>
                <span className='fw-bolder fs-6 text-dark'>{customer.firstname} {customer.lastname}</span>
              </div>
            </div>

            <div className='row mb-7'>
              <label className='col-lg-4 fw-bold text-muted'>
                Email
              </label>

              <div className='col-lg-8'>
                <span className='fw-bolder fs-6 text-dark'>{customer.email}</span>
              </div>
            </div>

            <div className='row mb-7'>
              <label className='col-lg-4 fw-bold text-muted'>
                Birthdate
              </label>

              <div className='col-lg-8'>
                <span className='fw-bolder fs-6 text-dark'>
                  {customer.birthdate && intl.formatDate(customer.birthdate, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>
            </div>
            {customer.oauth.length > 0 && (
              <div className="row">
                <h3>OAuth</h3>
                {customer.oauth.map(o => (
                   <div
                    key={o.uid}
                    className="d-flex col-lg-4 align-items-center p-3 bg-white rounded shadow-sm mb-3"
                  >
                    <img
                      src={o.picture}
                      alt="User avatar"
                      className="rounded-circle me-3 flex-shrink-0"
                      width={64}
                      height={64}
                    />
                    <div className="flex-grow-1">
                      {(o.firstname || o.lastname) && (
                        <div className="fw-semibold fs-5">
                          {o.firstname} {o.lastname}
                        </div>
                      )}
                      {o.username && (
                        <div className="text-muted small">@{o.username}</div>
                      )}
                      <div className="text-muted small">UID: <strong>{o.provider.toUpperCase()}</strong>-{o.uid}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

          </Card>
          {/* Start profile details */}
        </div>
      </div>

      <MsisdnList msisdns={customer.msisdns} />
      <div>
        <h1>Risk indicators</h1>
        <div className="d-flex flex-column flex-lg-row">
          {/* Begin left col */}
          <div className="d-flex flex-column gap-7 gap-lg-10 w-100 w-lg-350px mb-7 me-lg-10">
            <RiskCard<Isp>
              title="Detected ISP"
              items={customer.isp}
              getDate={(i) => new Date(i.date)}
              renderItem={(item, old) => (
                <span 
                  className={clsx('badge', { 'badge-light': old, 'badge-primary': !old })}
                  title={formatDate(intl, item.date)}
                >
                  {item.isp}
                </span>
              )}
            />

            <RiskCard<Device>
              title="Detected Devices"
              items={customer.devices}
              getDate={(d) => new Date(d.date)}
              renderItem={(item, old) => (
                <span 
                  className={clsx('badge', { 'badge-light': old, 'badge-primary': !old })}
                  title={formatDate(intl, item.date)}
                >
                  {`${item.os} / ${item.device} / ${item.browser}`}
                </span>
              )}
            />

            <RiskCard<IpData>
              title="Detected Risky IP"
              items={customer.riskyIps}
              getDate={(d) => new Date(d.date)}
              separator={true}
              renderItem={(ip, old) => (
                <div
                  title={formatDate(intl, ip.date)}
                  className="d-flex align-items-center"
                >
                  <span className={clsx('bullet bullet-vertical h-45px me-5', {
                    'bg-success': !old,
                    'bd-secondary': old
                  })}></span>
                  <div className='d-flex flex-column'>
                    <span className='text-gray-800 mb-1'>
                      {(['vpn', 'tor', 'proxy', 'robot', 'server'] as (keyof IpData)[]).map((d) => (
                        <span
                          key={d}
                          title={d.toUpperCase()}
                          className={clsx('badge me-1', {
                            'badge-secondary': !ip[d],
                            'badge-danger': ip[d],
                          })}
                        >
                          {d[0].toUpperCase()}
                        </span>
                      ))}
                      <span className={clsx('badge', {
                        'badge-success': ip.score < 30,
                        'badge-primary': ip.score >= 30 && ip.score < 50,
                        'badge-warning': ip.score >= 50 && ip.score < 70,
                        'badge-danger': ip.score >= 70,
                      })}>
                        {ip.score}
                      </span>
                      <span
                        className={clsx('ms-1 badge', {
                          'badge-success': ip.risk === 'low',
                          'badge-primary': ip.risk === 'medium',
                          'badge-warning': ip.risk === 'high',
                          'badge-danger': ip.risk === 'very high',
                        })}
                      >
                        {ip.risk}
                      </span>
                    </span>
                    <span className='text-gray-800 mb-1'>
                      <span className="fs-8">{ip.organization}</span>
                      <span className="fs-8">{ip.country}</span>
                      <span className="badge badge-secondary ms-2">{ip.ip}</span>
                    </span>
                  </div>
                </div>

              )}
            />
          </div>
          {/* End left col */}


          {/* Begin right col */}
          <div className="d-flex flex-column flex-row-fluid gap-7 gap-lg-10">
            {/* Start linked accounts */}
            <SharedUsersCard shared={customer?.shared ?? []} />
          </div>

        </div>
      </div>
    </>
  )
}

export default Overview
